import { Button, Card, Flex, Progress, Slider, Typography } from 'antd'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth'
import { useCompanyUsage, useUpdateCompanyMessageOverageSettings } from '../../company/hooks'

// Website visitor messages in this billing period over all websites, and the most a customer wants to pay for messages
// beyond the plan: off, or one of a few amounts in euros (stored as a number of messages).
export function MessageOverageSettingsCard() {
    const { activeCompany } = useAuth()
    const { t, i18n } = useTranslation()
    const locale = i18n.language.replace('_', '-')

    const companyId = activeCompany?.id
    const { data: usage } = useCompanyUsage(companyId ?? '')
    const { mutate, isPending } = useUpdateCompanyMessageOverageSettings(companyId)

    const messages = usage?.messages
    const steps = messages?.overageSteps ?? []

    // Slider position 0 is off; 1..n are the steps. A limit that is not a step (set by hand) shows as the nearest higher step.
    const stepFor = (limit: number | null | undefined, enabled: boolean | undefined) => {
        if (!enabled) {
            return 0
        }

        const index = steps.findIndex((step) => limit != null && step.messages >= limit)

        return index === -1 ? steps.length : index + 1
    }

    const [position, setPosition] = useState<number | null>(null)
    const current = position ?? stepFor(activeCompany?.messageOverageLimit, activeCompany?.messageOveragesEnabled)

    if (!activeCompany || !messages || !usage) {
        return null
    }

    // Whole euros without cents (€ 25), other amounts with them (€ 0,04).
    const money = (cents: number) =>
        (cents / 100).toLocaleString(locale, {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
            maximumFractionDigits: 2,
        })
    const selected = current > 0 ? steps[current - 1] : undefined

    return (
        <Card className="w-full">
            <Flex
                vertical
                gap="middle"
            >
                <div>
                    <Typography.Text type="secondary">{t('Pay-as-you-go')}</Typography.Text>
                    <Typography.Title
                        level={4}
                        className="mt-2 mb-0"
                    >
                        {t('Extra website messages')}
                    </Typography.Title>
                </div>

                <Flex
                    vertical
                    gap={4}
                >
                    <Typography.Text>
                        {t('{{used}} of {{allowance}} chat messages used this billing period', {
                            used: messages.usage.toLocaleString(locale),
                            allowance: messages.allowance.toLocaleString(locale),
                        })}
                        {' · '}
                        {t('{{sites}} of {{count}} websites connected', {
                            sites: usage.sites.connected,
                            count: usage.sites.limit,
                        })}
                    </Typography.Text>
                    <Progress
                        percent={Math.min(100, Math.round((messages.usage / Math.max(1, messages.allowance)) * 100))}
                        showInfo={false}
                    />
                    <Typography.Text type="secondary">
                        {messages.extraUsage > 0 &&
                            `${t('{{count}} extra messages', { count: messages.extraUsage })} · `}
                        {t('Your allowance renews on {{date}}.', { date: dayjs(usage.resetsAt).format('D MMMM') })}
                    </Typography.Text>
                </Flex>

                <Typography.Text type="secondary">
                    {t(
                        'When the messages of your plan are used, the chat keeps answering and each extra message costs {{price}}, excl. VAT. Choose the most you want to pay for that per month.',
                        { price: money(messages.overageUnitAmount) }
                    )}
                </Typography.Text>

                <Slider
                    min={0}
                    max={steps.length}
                    step={null}
                    value={current}
                    onChange={setPosition}
                    tooltip={{ open: false }}
                    marks={Object.fromEntries([
                        [0, t('Off')],
                        ...steps.map((step, index) => [index + 1, money(step.amount)]),
                    ])}
                />

                <Typography.Text strong>
                    {selected
                        ? t('At most {{amount}} per month extra, excl. VAT · {{messages}} messages', {
                              amount: money(selected.amount),
                              messages: selected.messages.toLocaleString(locale),
                          })
                        : t('Off: at the limit, visitors see that the chat is not available until it starts again.')}
                </Typography.Text>

                <Flex justify="flex-end">
                    <Button
                        type="primary"
                        loading={isPending}
                        onClick={() =>
                            mutate({
                                messageOveragesEnabled: selected !== undefined,
                                messageOverageLimit: selected?.messages ?? null,
                            })
                        }
                    >
                        {t('Save')}
                    </Button>
                </Flex>
            </Flex>
        </Card>
    )
}
